import { useEffect, useState, useMemo } from 'react';
import { UnifiedLayout } from '@shared/ui/layout';
import type { SidebarTab, BreadcrumbItem } from '@shared/ui/layout';
import { dedupeEntryCategories, getPrimaryMenuLabel } from '@shared/ui/layout';
import { getSidebarConfig } from '@/config/sidebar';
import { useAuthStore } from '@/stores/authStore';
import { useServiceStore } from '@/stores/serviceStore';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { getIconComponent } from '@/utils/iconMap';
import { useTenantPortalLogo } from '@/hooks/useTenantPortalLogo';
import { Shield } from 'lucide-react';
import { MenuPngIcon } from '@/lib/menuIcons';
import { usePortalTranslation, resolveServiceName, useUserMenuLabels } from '@shared/i18n';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, isAuthenticated, logout, refreshToken } = useAuthStore();
  const { accessibleServices } = useServiceStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [dynamicTabs, setDynamicTabs] = useState<SidebarTab[] | null>(null);
  const [dynamicCategoryOrder, setDynamicCategoryOrder] = useState<string[]>([]);
  const tenantLogo = useTenantPortalLogo();
  const { t: tAdmin, i18n } = usePortalTranslation('adminNav');
  const { t: tLayout } = usePortalTranslation('layout');
  const { t: tServices } = usePortalTranslation('services');
  const userMenuLabels = useUserMenuLabels();

  const sidebarConfig = useMemo(
    () => getSidebarConfig(location.pathname, user, tAdmin, i18n.language),
    [location.pathname, user, tAdmin, i18n.language],
  );

  // Entry 페이지에서 store의 접근 가능한 서비스 사용
  useEffect(() => {
    if (location.pathname !== '/entry') return;
    if (user?.userType === 'portal-admin') return;
    if (accessibleServices.length === 0) return;

    // 메뉴 그룹 = ITOps Admin 라벨(Service 마커 제외). PORTAL/SYSTEM은 하단.
    const categories = new Set<string>();
    accessibleServices.forEach((s) => {
      const primary = getPrimaryMenuLabel(s.labelType);
      if (primary) categories.add(primary);
    });
    const sortedCategories = dedupeEntryCategories(Array.from(categories));
    setDynamicCategoryOrder(sortedCategories);

    const tabs: SidebarTab[] = accessibleServices.map((service) => {
      const isInternal = ['it-service-request', 'tenant-admin'].includes(service.id);
      const internalPaths: Record<string, string> = {
        'it-service-request': '/it-service',
        'tenant-admin': '/tenant-admin',
      };

      return {
        id: service.id,
        label: resolveServiceName(service.id, service.label, tServices),
        icon: getIconComponent(service.iconName, Shield),
        href: isInternal ? internalPaths[service.id] : service.href,
        labelType: service.labelType,
      };
    });

    setDynamicTabs(tabs);
  }, [location.pathname, user?.userType, accessibleServices, tServices, i18n.language]);

  // 현재 경로에서 activeTab 추출
  const getActiveTab = () => {
    const path = location.pathname;
    const tabs = location.pathname === '/entry' && dynamicTabs 
      ? dynamicTabs 
      : sidebarConfig?.tabs;
    
    if (!tabs) return undefined;
    
    const exactMatch = tabs.find(tab => tab.href === path);
    if (exactMatch) return exactMatch.id;
    
    // 더 긴 경로부터 매칭 (정렬 후 찾기)
    const sortedTabs = [...tabs].sort((a, b) => b.href.length - a.href.length);
    const partialMatch = sortedTabs.find(tab => 
      tab.href !== '/' && path.startsWith(tab.href + '/')
    );
    return partialMatch?.id;
  };

  const handleLogout = () => {
    logout();
    const isAdminArea = location.pathname.startsWith('/admin');
    navigate({ to: isAdminArea ? '/admin/login' : '/login' });
  };

  const handleTabClick = (tab: SidebarTab) => {
    if (tab.href.startsWith('http')) {
      // 외부 서비스
      const { navigateToService } = useAuthStore.getState();
      navigateToService(tab.href);
    } else {
      // Entry(동적 서비스 목록)에서 서비스 탭은 Nginx가 다른 앱으로 라우팅하므로
      // 클라이언트 라우터 navigate 대신 전체 이동을 해야 Not Found를 피할 수 있다.
      const isEntryDynamicTab = location.pathname === '/entry' && !!dynamicTabs;
      const isPortalInternalPath =
        tab.href.startsWith('/entry') ||
        tab.href.startsWith('/admin') ||
        tab.href.startsWith('/it-service') ||
        tab.href.startsWith('/workflow-settings') ||
        tab.href === '/login' ||
        tab.href === '/';

      if (isEntryDynamicTab && !isPortalInternalPath) {
        const { navigateToService } = useAuthStore.getState();
        navigateToService(tab.href);
        return;
      }

      navigate({ to: tab.href });
    }
  };

  // Entry 페이지에서 동적 탭 사용
  const currentTabs = location.pathname === '/entry' && dynamicTabs 
    ? dynamicTabs 
    : sidebarConfig?.tabs;

  const currentServiceName = location.pathname === '/entry' && dynamicTabs
    ? ''
    : sidebarConfig?.serviceName ?? '';

  const currentServiceHref = location.pathname === '/entry' && dynamicTabs
    ? '/entry'
    : sidebarConfig?.serviceHref;

  // Entry 페이지에서 tenant 사용자인 경우 'PORTAL SERVICE' subtitle 추가
  const entrySubtitle = location.pathname === '/entry' && dynamicTabs && user?.userType === 'tenant-user'
    ? 'PORTAL SERVICE'
    : undefined;

  // 하위 경로 라벨 매핑
  const subPathLabels: Record<string, string> = useMemo(
    () => ({
      new: tLayout('breadcrumb.new'),
      edit: tLayout('breadcrumb.edit'),
    }),
    [tLayout, i18n.language],
  );

  // 경로 기반 breadcrumb 생성
  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    const items: BreadcrumbItem[] = [];
    const tabs = currentTabs || [];
    
    // 서비스명 추가
    if (currentServiceName) {
      items.push({ label: currentServiceName, href: currentServiceHref });
    }

    // 경로 세그먼트별로 처리
    let currentPath = '';
    for (let i = 1; i < segments.length; i++) {
      currentPath = '/' + segments.slice(0, i + 1).join('/');
      const segment = segments[i];
      const isLast = i === segments.length - 1;
      
      // 탭에서 라벨 찾기
      const tab = tabs.find(t => t.href === currentPath);
      if (tab) {
        items.push({ label: tab.label, href: isLast ? undefined : currentPath });
      } else {
        // 탭에 없으면 subPathLabels에서 찾거나 세그먼트 그대로 사용
        const label = subPathLabels[segment] || segment;
        items.push({ label, href: isLast ? undefined : currentPath });
      }
    }

    return items;
  }, [location.pathname, currentTabs, currentServiceName, currentServiceHref, subPathLabels]);

  const isItService = location.pathname.startsWith('/it-service');
  const currentCategoryOrder = location.pathname === '/entry' && dynamicTabs
    ? dynamicCategoryOrder
    : sidebarConfig?.categoryOrder;
  const headerIconSlug = sidebarConfig?.headerIconSlug;
  const expandedWidthRem = sidebarConfig?.expandedWidthRem ?? (isItService ? 18 : undefined);

  const sidebarProps = currentTabs ? {
    categoryOrder: currentCategoryOrder,
    tabs: currentTabs,
    activeTab: getActiveTab(),
    collapsible: true,
    onTabClick: handleTabClick,
    subtitle: entrySubtitle || currentServiceName,
    expandedWidthRem,
    headerIcon: headerIconSlug
      ? <MenuPngIcon slug={headerIconSlug} className="h-7 w-7 shrink-0 object-contain" />
      : isItService
        ? <MenuPngIcon slug="itsm-hero" className="h-7 w-7 shrink-0 object-contain" />
        : undefined,
  } : undefined;

  // logoText 결정: tenant 사용자인 경우 portalLogo 사용, 없으면 기본값
  const logoText = tenantLogo || 'IAMPAM PORTAL';

  // 로고 클릭 시 전체 새로고침 없이 클라이언트 라우팅 (back/재진입 시 저장소 재검사로 로그인 튕김 방지)
  const handleLogoClick = () => {
    if (user?.userType === 'portal-admin') {
      navigate({ to: '/admin' });
    } else {
      navigate({ to: '/entry' });
    }
  };

  return (
    <UnifiedLayout
      logoText={logoText}
      serviceName={currentServiceName}
      serviceHref={currentServiceHref}
      sidebar={sidebarProps}
      user={user}
      isAuthenticated={isAuthenticated}
      onLogoClick={handleLogoClick}
      onLogout={handleLogout}
      onExtendToken={async () => {
        const ok = await refreshToken();
        if (!ok) {
          alert(tLayout('session.extendFailed'));
        }
      }}
      topbarExtra={undefined}
      showLanguageToggle
      userMenuLabels={userMenuLabels}
      breadcrumbItems={breadcrumbItems}
    >
      <div className="p-6">
        {children}
      </div>
    </UnifiedLayout>
  );
}
